import React from "react";
import {
	App,
	Button,
	DatePicker,
	Empty,
	Input,
	Modal,
	Progress,
	Select,
	Segmented,
	Space,
	Tag,
	Tooltip,
	Tree,
	Typography,
	theme,
} from "antd";
import {
	PlusOutlined,
	DeleteOutlined,
	MinusOutlined,
	SwapOutlined,
	ClearOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import "./FlyerBulkSelectorModal.css";
import collect from "collect.js";
import icon from "@/assets/imgs/logo-icon.png";

// Import da moduli centralizzati
import { ACTIONS, DATE_FIELD_FORMAT } from "../constants/actions.js";
import { isBannerCard, normalizePrice, normalizeToArray } from "../utils/normalizers.js";
import { PRICE_FIELD_KEYS } from "../constants/fields.js";
import { toFieldKey } from "../utils/formatters.js";
import { useFieldColorMap, getFieldColor } from "@modules/fields/utils/fieldColors.js";

const { Text } = Typography;

function resolveOptionName(option, fallbackValue) {
	const candidates = [
		option?.name,
		option?.nome,
		option?.raw?.name,
		option?.raw?.nome,
		option?.raw?.label,
		typeof option?.label === "string" ? option.label : null,
		option?.value != null ? String(option.value) : null,
		fallbackValue != null ? String(fallbackValue) : null,
	];
	const match = candidates.find((value) => typeof value === "string" && value.trim());
	return match || "";
}

function parseRangeValue(value) {
	if (typeof value !== "string")
		return null;

	const parts = value.split(/[_|]/).map((part) => part.trim()).filter(Boolean);
	if (parts.length !== 2)
		return null;

	const start = dayjs(parts[0]);
	const end = dayjs(parts[1]);
	if (!start.isValid() || !end.isValid())
		return null;

	return [start, end];
}

function formatDateTagLabel(value) {
	if (typeof value !== "string") {
		if (value == null)
			return "";
		return String(value);
	}

	const parsedRange = parseRangeValue(value);
	if (parsedRange) {
		const [start, end] = parsedRange;
		return `${start.format("DD/MM/YYYY")} → ${end.format("DD/MM/YYYY")}`;
	}

	const parsedSingle = dayjs(value);
	if (parsedSingle.isValid())
		return parsedSingle.format("DD/MM/YYYY");

	return value;
}

function isDateValueValid(value, mode) {
	if (mode === "range") {
		if (!Array.isArray(value) || value.length < 2)
			return false;
		const [start, end] = value;
		return dayjs.isDayjs(start) && start.isValid() && dayjs.isDayjs(end) && end.isValid();
	}
	return dayjs.isDayjs(value) && value.isValid();
}

function formatDateValueForPayload(value, mode) {
	if (!isDateValueValid(value, mode))
		return "";

	if (mode === "range") {
		const [start, end] = value;
		return `${start.format(DATE_FIELD_FORMAT)}_${end.format(DATE_FIELD_FORMAT)}`;
	}

	return value.format(DATE_FIELD_FORMAT);
}

function ActionSegLabel({ icon, text, tip }) {
	return (
		<Tooltip title={tip}>
			<span className="fb-seg-opt">{icon}<span>{text}</span></span>
		</Tooltip>
	);
}

// Opzioni per i campi a valore singolo (Categoria, Classe, Fondi) e per le date:
// "Aggiungi" non è applicabile perché il campo contiene un solo valore.
const SINGLE_ACTION_OPTIONS = [
	{
		value: ACTIONS.SKIP,
		label: <ActionSegLabel icon={<MinusOutlined />} text="Lascia" tip="Non modifica il campo: ogni card mantiene il valore che ha già." />,
	},
	{
		value: ACTIONS.SET,
		label: <ActionSegLabel icon={<SwapOutlined />} text="Sostituisci" tip="Imposta il valore scelto, sostituendo quello eventualmente già presente." />,
	},
	{
		value: ACTIONS.CLEAR,
		label: <ActionSegLabel icon={<ClearOutlined />} text="Svuota" tip="Rimuove il valore dal campo su tutte le card selezionate." />,
	},
];

// Opzioni per i campi multi-valore (Bollino, Filtro, Meccanica):
// "Aggiungi" unisce i valori scelti a quelli già presenti, senza sostituirli.
const MULTI_ACTION_OPTIONS = [
	{
		value: ACTIONS.SKIP,
		label: <ActionSegLabel icon={<MinusOutlined />} text="Lascia" tip="Non modifica il campo: ogni card mantiene i valori che ha già." />,
	},
	{
		value: ACTIONS.SET,
		label: <ActionSegLabel icon={<SwapOutlined />} text="Sostituisci" tip="Sostituisce TUTTI i valori esistenti con quelli scelti." />,
	},
	{
		value: ACTIONS.ADD,
		label: <ActionSegLabel icon={<PlusOutlined />} text="Aggiungi" tip="Aggiunge i valori scelti a quelli già presenti su ogni card, senza rimuovere gli altri." />,
	},
	{
		value: ACTIONS.CLEAR,
		label: <ActionSegLabel icon={<ClearOutlined />} text="Svuota" tip="Rimuove tutti i valori dal campo su tutte le card selezionate." />,
	},
];

// Le sei tassonomie storiche hanno righe dedicate qui sotto; ogni altro campo
// SELECT/MULTISELECT abilitato per la cooperativa (focus, stile, variante, fondo...)
// è una tassonomia generica e viene reso dinamicamente a partire da `productFields`.
const LEGACY_TAXONOMY_KEYS = new Set(["categoria", "classe", "fondi", "bollino", "filtro", "meccanica"]);
const EMPTY_GENERIC_TAXONOMIES = {};

// Campi di testo esclusi dalla modifica massiva: identificativi univoci del prodotto,
// scriverli uguali su più card non ha mai senso.
const TEXT_FIELDS_EXCLUDED = new Set(["ean", "codice_cliente"]);

// Campi di testo: "Aggiungi in coda" accoda il testo a quello già presente su ogni card
// (separatore: a capo per i TEXTAREA, spazio per i TEXT). Non vale per i prezzi.
const TEXT_ACTION_OPTIONS = [
	{
		value: ACTIONS.SKIP,
		label: <ActionSegLabel icon={<MinusOutlined />} text="Lascia" tip="Non modifica il campo: ogni card mantiene il testo che ha già." />,
	},
	{
		value: ACTIONS.SET,
		label: <ActionSegLabel icon={<SwapOutlined />} text="Sostituisci" tip="Scrive il testo indicato su tutte le card selezionate, sostituendo quello presente." />,
	},
	{
		value: ACTIONS.ADD,
		label: <ActionSegLabel icon={<PlusOutlined />} text="Aggiungi in coda" tip="Accoda il testo indicato a quello già presente su ogni card, senza cancellarlo." />,
	},
	{
		value: ACTIONS.CLEAR,
		label: <ActionSegLabel icon={<ClearOutlined />} text="Svuota" tip="Cancella il testo del campo su tutte le card selezionate." />,
	},
];
const PRICE_ACTION_OPTIONS = TEXT_ACTION_OPTIONS.filter((opt) => opt.value !== ACTIONS.ADD);

const ACTION_SUMMARY_LABELS = {
	[ACTIONS.SET]: "sostituisci",
	[ACTIONS.ADD]: "aggiungi in coda",
	[ACTIONS.CLEAR]: "svuota",
};


export default function FlyerBulkSelectorModal({
	open,
	onClose,
	pages = [],
	categoryOptions = [],
	classOptions = [],
	fundOptions = [],
	badgeOptions = [],
	filterOptions = [],
	mechanicOptions = [],
	productFields = [],
	genericTaxonomies = EMPTY_GENERIC_TAXONOMIES,
	loading = false,
	onApply,
	onMove,
	onDelete,
	getImageUrl,
	onCreateCategory,
	onCreateClass,
	onCreateFund,
	onCreateBadge,
	onCreateFilter,
	onCreateMechanic,
}) {
	const { token } = theme.useToken();
	const [checkedKeys, setCheckedKeys] = React.useState([]);
	const [expandedKeys, setExpandedKeys] = React.useState([]);

	const [categoryAction, setCategoryAction] = React.useState(ACTIONS.SKIP);
	const [categoryValue, setCategoryValue] = React.useState();
	const [classAction, setClassAction] = React.useState(ACTIONS.SKIP);
	const [classValue, setClassValue] = React.useState();
	const [fundAction, setFundAction] = React.useState(ACTIONS.SKIP);
	const [fundValue, setFundValue] = React.useState();
	const [badgeAction, setBadgeAction] = React.useState(ACTIONS.SKIP);
	const [badgeValue, setBadgeValue] = React.useState([]);
	const [filterAction, setFilterAction] = React.useState(ACTIONS.SKIP);
	const [filterValue, setFilterValue] = React.useState([]);
	const [mechanicAction, setMechanicAction] = React.useState(ACTIONS.SKIP);
	const [mechanicValue, setMechanicValue] = React.useState([]);
	const [dateActionsByKey, setDateActionsByKey] = React.useState({});
	const [dateValuesByKey, setDateValuesByKey] = React.useState({});
	const [categorySearch, setCategorySearch] = React.useState("");
	const [classSearch, setClassSearch] = React.useState("");
	const [fundSearch, setFundSearch] = React.useState("");
	const [badgeSearch, setBadgeSearch] = React.useState("");
	const [filterSearch, setFilterSearch] = React.useState("");
	const [mechanicSearch, setMechanicSearch] = React.useState("");
	// Tassonomie generiche: un'unica mappa per campo invece di uno stato per campo.
	const [genericActionsByKey, setGenericActionsByKey] = React.useState({});
	const [genericValuesByKey, setGenericValuesByKey] = React.useState({});
	const [genericSearchByKey, setGenericSearchByKey] = React.useState({});
	const [textActionsByKey, setTextActionsByKey] = React.useState({});
	const [textValuesByKey, setTextValuesByKey] = React.useState({});
	const { modal } = App.useApp();

	const allowedFields = React.useMemo(() => {
		if (!Array.isArray(productFields) || productFields.length === 0) {
			return {
				categoria: false,
				classe: false,
				fondi: false,
				bollino: false,
				filtro: false,
				meccanica: false,
			};
		}

		const fieldKeys = new Set(
			productFields.map((field) => toFieldKey(field?.key || field?.name || ""))
		);

		return {
			categoria: fieldKeys.has("categoria"),
			classe: fieldKeys.has("classe"),
			fondi: fieldKeys.has("fondi"),
			bollino: fieldKeys.has("bollino"),
			filtro: fieldKeys.has("filtro"),
			meccanica: fieldKeys.has("meccanica"),
		};
	}, [productFields]);

	const dateFields = React.useMemo(() => {
		if (!Array.isArray(productFields) || productFields.length === 0)
			return [];

		const seen = new Set();
		const fields = [];
		for (const field of productFields) {
			const rawKey = field?.key || field?.name;
			const key = toFieldKey(rawKey);
			if (!key || seen.has(key))
				continue;

			const rawType = String(field?.type || field?.inputType || "").toUpperCase();
			if (rawType !== "DATE")
				continue;

			seen.add(key);
			fields.push({
				key,
				label: field?.name || field?.label || key,
				dateMode: field?.dateMode === "range" ? "range" : "single",
			});
		}
		return fields;
	}, [productFields]);

	// Tassonomie generiche abilitate per la cooperativa: stesso ordine di `productFields`,
	// cardinalità dal tipo del campo (MULTISELECT = più valori, SELECT = valore singolo).
	// Servono anche i valori in anagrafica (`genericTaxonomies`), altrimenti non c'è nulla
	// da proporre nella select.
	const genericFields = React.useMemo(() => {
		if (!Array.isArray(productFields) || productFields.length === 0)
			return [];

		const seen = new Set();
		const fields = [];
		for (const field of productFields) {
			const key = toFieldKey(field?.key || field?.name);
			if (!key || seen.has(key) || LEGACY_TAXONOMY_KEYS.has(key) || !genericTaxonomies[key])
				continue;

			const rawType = String(field?.type || field?.inputType || "").toUpperCase();
			if (rawType !== "SELECT" && rawType !== "MULTISELECT")
				continue;

			seen.add(key);
			fields.push({
				key,
				label: field?.name || field?.label || key,
				multiple: rawType === "MULTISELECT",
			});
		}
		return fields;
	}, [productFields, genericTaxonomies]);

	const genericSelectOptionsByKey = React.useMemo(() => {
		const result = {};
		genericFields.forEach((field) => {
			result[field.key] = (genericTaxonomies[field.key]?.options || []).map((opt) => {
				const rawValue = opt?.value ?? opt?.id ?? opt?._id;
				if (rawValue == null) return null;
				const value = String(rawValue);
				const name = resolveOptionName(opt, value);
				return {
					value,
					label: <span className="fb-select-option-label">{name}</span>,
					name,
					searchValue: name.toLowerCase(),
				};
			}).filter(Boolean);
		});
		return result;
	}, [genericFields, genericTaxonomies]);

	// Campi di testo abilitati per la cooperativa (TEXT/TEXTAREA), esclusi gli identificativi.
	const textFields = React.useMemo(() => {
		if (!Array.isArray(productFields) || productFields.length === 0)
			return [];

		const seen = new Set();
		const fields = [];
		for (const field of productFields) {
			const key = toFieldKey(field?.key || field?.name);
			if (!key || seen.has(key) || TEXT_FIELDS_EXCLUDED.has(key))
				continue;

			const rawType = String(field?.type || field?.inputType || "").toUpperCase();
			if (rawType !== "TEXT" && rawType !== "TEXTAREA")
				continue;

			seen.add(key);
			fields.push({
				key,
				label: field?.name || field?.label || key,
				multiline: rawType === "TEXTAREA",
				isPrice: PRICE_FIELD_KEYS.has(key),
			});
		}
		return fields;
	}, [productFields]);

	// Setter per-campo compatibili con quelli di useState (accettano anche un updater),
	// così le righe generiche riusano renderActionRow/renderMultiActionRow invariati.
	const makeKeyedSetter = React.useCallback((setMap, key) => (next) => {
		setMap((prev) => ({
			...prev,
			[key]: typeof next === "function" ? next(prev[key]) : next,
		}));
	}, []);

	const handleClose = React.useCallback(() => {
		// Reset all state when modal is closed
		setCheckedKeys([]);
		setExpandedKeys([]);
		setCategoryAction(ACTIONS.SKIP);
		setCategoryValue();
		setClassAction(ACTIONS.SKIP);
		setClassValue();
		setFundAction(ACTIONS.SKIP);
		setFundValue();
		setBadgeAction(ACTIONS.SKIP);
		setBadgeValue([]);
		setFilterAction(ACTIONS.SKIP);
		setFilterValue([]);
		setMechanicAction(ACTIONS.SKIP);
		setMechanicValue([]);
		setDateActionsByKey({});
		setDateValuesByKey({});
		setCategorySearch("");
		setClassSearch("");
		setFundSearch("");
		setBadgeSearch("");
		setFilterSearch("");
		setMechanicSearch("");
		setGenericActionsByKey({});
		setGenericValuesByKey({});
		setGenericSearchByKey({});
		setTextActionsByKey({});
		setTextValuesByKey({});
		onClose?.();
	}, [onClose]);

	const categorySelectOptions = React.useMemo(() => {
		return categoryOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [categoryOptions]);

	const classSelectOptions = React.useMemo(() => {
		return classOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [classOptions]);

	const fundSelectOptions = React.useMemo(() => {
		return fundOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [fundOptions]);

	const badgeSelectOptions = React.useMemo(() => {
		return badgeOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [badgeOptions]);

	const filterSelectOptions = React.useMemo(() => {
		return filterOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [filterOptions]);

	const mechanicSelectOptions = React.useMemo(() => {
		return mechanicOptions.map((opt) => {
			const rawValue = opt?.value ?? opt?.id ?? opt?._id;
			if (rawValue == null) return null;
			const value = String(rawValue);
			const name = resolveOptionName(opt, value);
			const imageUrl = opt?.imageUrl || opt?.raw?.imageUrl || null;
			const label = (
				<Space size={8} className="fb-select-option">
					<img src={imageUrl || icon} alt="" className="fb-select-thumb" />
					<span className="fb-select-option-label">{name}</span>
				</Space>
			);
			const searchValue = String(opt?.searchValue || name || "").toLowerCase();
			return {
				value,
				label,
				searchValue,
				raw: opt,
			};
		}).filter(Boolean);
	}, [mechanicOptions]);

	const handleCreateCategoryOption = React.useCallback(async () => {
		const value = categorySearch.trim();
		if (!value || !onCreateCategory) return null;
		try {
			const created = await onCreateCategory(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setCategoryAction(ACTIONS.SET);
				setCategoryValue(String(newValue));
			}
			return created ?? null;
		}
		finally {
			setCategorySearch("");
		}
	}, [categorySearch, onCreateCategory]);

	const handleCreateClassOption = React.useCallback(async () => {
		const value = classSearch.trim();
		if (!value || !onCreateClass) return null;
		try {
			const created = await onCreateClass(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setClassAction(ACTIONS.SET);
				setClassValue(String(newValue));
			}
			return created ?? null;
		}
		finally {
			setClassSearch("");
		}
	}, [classSearch, onCreateClass]);

	const handleCreateFundOption = React.useCallback(async () => {
		const value = fundSearch.trim();
		if (!value || !onCreateFund) return null;
		try {
			const created = await onCreateFund(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setFundAction(ACTIONS.SET);
				setFundValue(String(newValue));
			}
			return created ?? null;
		}
		finally {
			setFundSearch("");
		}
	}, [fundSearch, onCreateFund]);

	const handleCreateBadgeOption = React.useCallback(async () => {
		const value = badgeSearch.trim();
		if (!value || !onCreateBadge) return null;
		try {
			const created = await onCreateBadge(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setBadgeAction((prev) => (prev === ACTIONS.ADD ? ACTIONS.ADD : ACTIONS.SET));
				setBadgeValue((prev) => {
					const next = normalizeToArray(prev);
					if (!next.includes(String(newValue)))
						next.push(String(newValue));
					return next;
				});
			}
			return created ?? null;
		}
		finally {
			setBadgeSearch("");
		}
	}, [badgeSearch, onCreateBadge]);

	const handleCreateFilterOption = React.useCallback(async () => {
		const value = filterSearch.trim();
		if (!value || !onCreateFilter) return null;
		try {
			const created = await onCreateFilter(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setFilterAction((prev) => (prev === ACTIONS.ADD ? ACTIONS.ADD : ACTIONS.SET));
				setFilterValue((prev) => {
					const next = normalizeToArray(prev);
					if (!next.includes(String(newValue)))
						next.push(String(newValue));
					return next;
				});
			}
			return created ?? null;
		}
		finally {
			setFilterSearch("");
		}
	}, [filterSearch, onCreateFilter]);

	const handleCreateMechanicOption = React.useCallback(async () => {
		const value = mechanicSearch.trim();
		if (!value || !onCreateMechanic) return null;
		try {
			const created = await onCreateMechanic(value);
			const newValue = created?.value ?? created?.id ?? created?._id;
			if (newValue != null) {
				setMechanicAction((prev) => (prev === ACTIONS.ADD ? ACTIONS.ADD : ACTIONS.SET));
				setMechanicValue((prev) => {
					const next = normalizeToArray(prev);
					if (!next.includes(String(newValue)))
						next.push(String(newValue));
					return next;
				});
			}
			return created ?? null;
		}
		finally {
			setMechanicSearch("");
		}
	}, [mechanicSearch, onCreateMechanic]);

	const fieldColorMap = useFieldColorMap();
	const resolveTagColor = React.useCallback((option, fallbackColor) => {
		if (!option) return fallbackColor;
		return option.tagColor || option.color || fallbackColor;
	}, []);

	const gabbiaOnlyPages = React.useMemo(() => {
		return pages.map((page) => {
			if (!page || typeof page !== "object") {
				return page;
			}

			// Includiamo tutti gli item (gabbia + banner) ordinati per crop
			const sortedItems = collect(page.items || [])
				.sortBy("crop")
				.all();

			return {
				...page,
				items: sortedItems,
			};
		});
	}, [pages]);

	const cardKeyMap = React.useMemo(() => {
		const map = new Map();

		gabbiaOnlyPages.forEach((page) => {
			(page?.items || []).forEach((item) => {
				if (!item?._id) return;
				map.set(String(item._id), item);
			});
		});

		return map;
	}, [gabbiaOnlyPages]);

	const treeData = React.useMemo(() => {
		const categoryMap = new Map(categoryOptions.map((opt) => [String(opt.value), opt]),);
		const classMap = new Map(classOptions.map((opt) => [String(opt.value), opt]),);
		const fundMap = new Map(fundOptions.map((opt) => [String(opt.value), opt]),);
		const badgeMap = new Map(badgeOptions.map((opt) => [String(opt.value), opt]),);
		const filterMap = new Map(filterOptions.map((opt) => [String(opt.value), opt]),);
		const mechanicMap = new Map(mechanicOptions.map((opt) => [String(opt.value), opt]),);
		const genericOptionMaps = new Map(genericFields.map((field) => [
			field.key,
			new Map((genericSelectOptionsByKey[field.key] || []).map((opt) => [opt.value, opt])),
		]));

		const toTagList = (ai) => {
			if (!ai) return [];
			const tags = [];
			if (ai.categoria) {
				const opt = categoryMap.get(String(ai.categoria));
				tags.push({
					key: `cat-${ai.categoria}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "categoria", "green")),
					type: "Categoria",
					label:
						opt?.name ||
						opt?.label ||
						ai.categoriaNome ||
						ai.categoriaLabel ||
						ai.categoria,
				});
			}
			if (ai.classe) {
				const opt = classMap.get(String(ai.classe));
				tags.push({
					key: `cls-${ai.classe}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "classe", "blue")),
					type: "Classe",
					label:
						opt?.name ||
						opt?.label ||
						ai.classeNome ||
						ai.classeLabel ||
						ai.classe,
				});
			}
			if (ai.fondi) {
				const opt = fundMap.get(String(ai.fondi));
				tags.push({
					key: `fnd-${ai.fondi}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "fondi", "cyan")),
					type: "Fondi",
					label:
						opt?.name ||
						opt?.label ||
						ai.fondiNome ||
						ai.fondiLabel ||
						ai.fondi,
				});
			}
			const bollini = normalizeToArray(ai.bollino);
			bollini.forEach((bollinoId, index) => {
				const opt = badgeMap.get(String(bollinoId));
				tags.push({
					key: `bdg-${bollinoId}-${index}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "bollino", "orange")),
					type: "Bollino",
					label:
						opt?.name ||
						opt?.label ||
						bollinoId,
				});
			});
			const filtri = normalizeToArray(ai.filtro);
			filtri.forEach((filtroId, index) => {
				const opt = filterMap.get(String(filtroId));
				tags.push({
					key: `flt-${filtroId}-${index}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "filtro", "purple")),
					type: "Filtro",
					label: opt?.name || opt?.label || filtroId,
				});
			});
				const meccaniche = normalizeToArray(ai.meccanica);
				meccaniche.forEach((meccanicaId, index) => {
					const opt = mechanicMap.get(String(meccanicaId));
					tags.push({
					key: `mec-${meccanicaId}-${index}`,
					color: resolveTagColor(opt, getFieldColor(fieldColorMap, "meccanica", "geekblue")),
					type: "Meccanica",
						label: opt?.name || opt?.label || meccanicaId,
					});
				});
				dateFields.forEach((field) => {
					const rawValue = ai?.[field.key];
					if (rawValue == null || rawValue === "")
						return;
					tags.push({
						key: `dt-${field.key}-${String(rawValue)}`,
						color: "purple",
						type: field.label,
						label: formatDateTagLabel(rawValue),
					});
				});
				genericFields.forEach((field) => {
					const optionMap = genericOptionMaps.get(field.key);
					normalizeToArray(ai?.[field.key]).forEach((valueId, index) => {
						if (valueId == null || valueId === "")
							return;
						tags.push({
							key: `gen-${field.key}-${valueId}-${index}`,
							color: getFieldColor(fieldColorMap, field.key, "blue"),
							type: field.label,
							label: optionMap?.get(String(valueId))?.name || String(valueId),
						});
					});
				});
				return tags;
			};

		return gabbiaOnlyPages.map((page) => {
			const pageKey = `page-${page.page}`;
			return {
				key: pageKey,
				title: (
					<PageNode
						page={page.page}
						count={page.items?.length || 0}
						colorBg={token.colorFillTertiary}
					/>
				),
				children: (page.items || []).map((item) => {
					const itemKey = String(item._id);
					return {
						key: itemKey,
						title: (
							<CardNode
								item={item}
								getImageUrl={getImageUrl}
								// Un banner tiene la categoria in banner.*, come la sua scheda e l'XML:
								// i valori in ai.* su un banner non escono da nessuna parte.
								tags={toTagList(isBannerCard(item) ? { categoria: item?.banner?.categoria } : item.ai)}
							/>
						),
					};
				}),
			};
		});
	}, [
		gabbiaOnlyPages,
		categoryOptions,
		classOptions,
		fundOptions,
		badgeOptions,
			filterOptions,
			mechanicOptions,
			dateFields,
			genericFields,
			genericSelectOptionsByKey,
			token.colorFillTertiary,
			getImageUrl,
			resolveTagColor,
			fieldColorMap,
		]);

	const leafKeys = React.useMemo(() => {
		return treeData.flatMap((node) =>
			(node.children || []).map((child) => String(child.key)));
	}, [treeData]);

	const totalProducts = React.useMemo(() => leafKeys.length, [leafKeys]);

	const selectionCount = React.useMemo(() => {
		return checkedKeys.filter((key) => cardKeyMap.has(String(key))).length;
	}, [checkedKeys, cardKeyMap]);

	const selectionPercent = React.useMemo(() => {
		if (!totalProducts) return 0;
		return Math.round((selectionCount / totalProducts) * 100);
	}, [selectionCount, totalProducts]);

	const handleExpandAll = React.useCallback(() => {
		setExpandedKeys(treeData.map((node) => String(node.key)));
	}, [treeData]);

	const handleCollapseAll = React.useCallback(() => {
		setExpandedKeys([]);
	}, []);

	const handleSelectAll = React.useCallback(() => {
		setCheckedKeys(leafKeys);
	}, [leafKeys]);

	const handleClearSelection = React.useCallback(() => {
		setCheckedKeys([]);
	}, []);

	React.useEffect(() => {
		if (open) {
			const keys = treeData.map((node) => node.key);
			setExpandedKeys(keys);
		}
	}, [open]); // Rimosso treeData dalle dipendenze per evitare re-espansione ad ogni update

	// Handler per drag & drop
	const handleDrop = React.useCallback((info) => {
		if (!onMove) return;

		const dragKey = String(info.dragNode.key);
		const dropKey = String(info.node.key);

		// Helper per trovare info reali della card aggiornate
		const findCardInfo = (key) => {
			for (const p of gabbiaOnlyPages) {
				const idx = (p.items || []).findIndex(item => String(item._id) === key);
				if (idx !== -1) {
					return {
						page: p.page,
						index: idx, // 0-based
						position: idx + 1, // 1-based
						item: p.items[idx],
						totalItems: p.items.length
					};
				}
			}
			return null;
		};

		// 1. Identifica Sorgente
		if (dragKey.startsWith("page-")) return; // Non spostiamo pagine
		const sourceInfo = findCardInfo(dragKey);
		if (!sourceInfo) return; // Card sorgente non trovata

		// 2. Identifica Destinazione e Calcola Posizione
		let targetPage;
		let targetPosition;

		// Calcolo posizione relativa standard Ant Design Tree
		const dropPos = info.node.pos.split('-');
		const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

		// Caso A: Drop su Header Pagina
		if (dropKey.startsWith("page-")) {
			targetPage = parseInt(dropKey.replace("page-", ""), 10);
			// Se droppiamo sull'header, inseriamo sempre all'inizio della pagina
			// Questo è il comportamento più prevedibile per "metti in questa pagina"
			targetPosition = 1;
		}
		// Caso B: Drop su/vicino a una Card
		else {
			const targetCardInfo = findCardInfo(dropKey);
			if (!targetCardInfo) return; // Target non trovato

			targetPage = targetCardInfo.page;

			// dropPosition: -1 = sopra, 0 = su (dentro), 1 = sotto
			if (dropPosition === -1) {
				// Sopra la card target -> Prende la sua posizione
				targetPosition = targetCardInfo.position;
			} else if (dropPosition === 1) {
				// Sotto la card target -> Prende posizione successiva
				targetPosition = targetCardInfo.position + 1;
			} else {
				// (0) Drop "sopra" la card (senza gap) -> Sostituisce/Prende posizione
				targetPosition = targetCardInfo.position;
			}
		}

		// Assicura posizione valida
		targetPosition = Math.max(1, targetPosition);

		// 3. Compensazione Same-Page Downwards
		// Se spostiamo nella stessa pagina e la destinazione è DOPO la sorgente,
		// dobbiamo decrementare di 1 perché l'elemento viene rimosso prima dell'inserimento.
		const isSamePage = sourceInfo.page === targetPage;
		const isMovingDown = targetPosition > sourceInfo.position;

		if (isSamePage && isMovingDown) {
			targetPosition -= 1;
		}

		// 4. Esegui spostamento se necessario
		if (isSamePage && sourceInfo.position === targetPosition) return; // Nessun cambiamento

		onMove({
			cardId: dragKey,
			targetPage,
			targetPosition
		});

	}, [onMove, gabbiaOnlyPages]);

	// Handler per eliminazione bulk
	const handleBulkDelete = React.useCallback(() => {
		if (!onDelete) return;

		const selectedCardIds = checkedKeys.filter((key) =>
			cardKeyMap.has(String(key)),
		);

		if (!selectedCardIds.length) return;

		// Passa gli ID da eliminare e una callback per pulire la selezione
		onDelete(selectedCardIds.map((key) => String(key)), () => {
			// Rimuovi le card eliminate dai checkedKeys
			setCheckedKeys((prev) => {
				const deletedSet = new Set(selectedCardIds);
				return prev.filter((key) => !deletedSet.has(String(key)));
			});
		});
	}, [onDelete, checkedKeys, cardKeyMap]);

	const handleApply = React.useCallback(() => {
		const selectedCardIds = checkedKeys.filter((key) =>
			cardKeyMap.has(String(key)),
		);
		if (!selectedCardIds.length) return;

		const dateUpdates = {};
		dateFields.forEach((field) => {
			const action = dateActionsByKey[field.key] ?? ACTIONS.SKIP;
			if (action === ACTIONS.SET) {
				dateUpdates[field.key] = formatDateValueForPayload(
					dateValuesByKey[field.key] ?? null,
					field.dateMode,
				);
				return;
			}
			if (action === ACTIONS.CLEAR)
				dateUpdates[field.key] = "";
		});

		// "Aggiungi" (ADD) non può finire in `updates`: il valore finale dipende dai
		// valori già presenti su OGNI card, quindi l'unione va calcolata per-card a valle
		// (FlyerManagePage.handleBulkApply). Qui passiamo solo i valori da aggiungere.
		const additions = {};
		if (badgeAction === ACTIONS.ADD) {
			const vals = normalizeToArray(badgeValue);
			if (vals.length) additions.bollino = vals;
		}
		if (filterAction === ACTIONS.ADD) {
			const vals = normalizeToArray(filterValue);
			if (vals.length) additions.filtro = vals;
		}
		if (mechanicAction === ACTIONS.ADD) {
			const vals = normalizeToArray(mechanicValue);
			if (vals.length) additions.meccanica = vals;
		}

		const genericUpdates = {};
		genericFields.forEach((field) => {
			const action = genericActionsByKey[field.key] ?? ACTIONS.SKIP;
			const value = genericValuesByKey[field.key];
			if (field.multiple) {
				if (action === ACTIONS.SET)
					genericUpdates[field.key] = normalizeToArray(value);
				else if (action === ACTIONS.CLEAR)
					genericUpdates[field.key] = [];
				else if (action === ACTIONS.ADD) {
					const vals = normalizeToArray(value);
					if (vals.length) additions[field.key] = vals;
				}
				return;
			}
			if (action === ACTIONS.SET)
				genericUpdates[field.key] = value ?? null;
			else if (action === ACTIONS.CLEAR)
				genericUpdates[field.key] = null;
		});

		// Testi: SET/CLEAR sono valori finali uguali per tutte le card; "Aggiungi in coda"
		// dipende dal testo già presente su OGNI card, quindi come le additions va risolto
		// per-card a valle (FlyerManagePage.handleBulkApply).
		const textUpdates = {};
		const appends = {};
		const textSummary = [];
		textFields.forEach((field) => {
			const action = textActionsByKey[field.key] ?? ACTIONS.SKIP;
			if (action === ACTIONS.SKIP)
				return;
			const text = String(textValuesByKey[field.key] ?? "").trim();
			if (action === ACTIONS.SET)
				textUpdates[field.key] = field.isPrice ? normalizePrice(text) : text;
			else if (action === ACTIONS.CLEAR)
				textUpdates[field.key] = "";
			else if (action === ACTIONS.ADD && !field.isPrice)
				appends[field.key] = { text, separator: field.multiline ? "\n" : " " };
			textSummary.push(`${field.label} (${ACTION_SUMMARY_LABELS[action]})`);
		});

		const payload = {
			ids: selectedCardIds.map((key) => String(key)),
			updates: {
				categoria:
					categoryAction === ACTIONS.SET
						? (categoryValue ?? null)
						: categoryAction === ACTIONS.CLEAR
							? null
							: undefined,
				classe:
					classAction === ACTIONS.SET
						? (classValue ?? null)
						: classAction === ACTIONS.CLEAR
							? null
							: undefined,
				fondi:
					fundAction === ACTIONS.SET
						? (fundValue ?? null)
						: fundAction === ACTIONS.CLEAR
							? null
							: undefined,
				bollino:
					badgeAction === ACTIONS.SET
						? normalizeToArray(badgeValue)
						: badgeAction === ACTIONS.CLEAR
							? []
							: undefined,
				filtro:
					filterAction === ACTIONS.SET
						? normalizeToArray(filterValue)
						: filterAction === ACTIONS.CLEAR
							? []
							: undefined,
				meccanica:
					mechanicAction === ACTIONS.SET
						? normalizeToArray(mechanicValue)
						: mechanicAction === ACTIONS.CLEAR
							? []
							: undefined,
				...dateUpdates,
				...genericUpdates,
				...textUpdates,
			},
			additions,
			appends,
		};

		if (!textSummary.length) {
			onApply?.(payload);
			return;
		}

		// Sui testi un errore sovrascrive contenuti diversi card per card e non c'è annulla:
		// riepilogo esplicito prima di salvare.
		const count = payload.ids.length;
		modal.confirm({
			title: `Modificare i testi di ${count} ${count === 1 ? "prodotto" : "prodotti"}?`,
			content: (
				<div>
					<p style={{ marginBottom: 8 }}>Campi di testo coinvolti:</p>
					<ul style={{ paddingLeft: 18, marginBottom: 8 }}>
						{textSummary.map((line) => <li key={line}>{line}</li>)}
					</ul>
					<p style={{ marginBottom: 0 }}>Il testo attuale di questi campi verrà modificato su tutti i prodotti selezionati e l'operazione non si può annullare.</p>
				</div>
			),
			okText: "Applica",
			cancelText: "Annulla",
			onOk: () => onApply?.(payload),
		});
	}, [
		textFields,
		textActionsByKey,
		textValuesByKey,
		modal,
		genericFields,
		genericActionsByKey,
		genericValuesByKey,
		checkedKeys,
		cardKeyMap,
		categoryAction,
		categoryValue,
		classAction,
		classValue,
		fundAction,
		fundValue,
		badgeAction,
		badgeValue,
		filterAction,
		filterValue,
		mechanicAction,
		mechanicValue,
		dateFields,
		dateActionsByKey,
		dateValuesByKey,
		onApply,
	]);

	const hasChanges = React.useMemo(() => {
		const fixedUpdates = [
			categoryAction,
			classAction,
			fundAction,
			badgeAction,
			filterAction,
			mechanicAction,
		];
		const dateUpdates = dateFields.map((field) => dateActionsByKey[field.key] ?? ACTIONS.SKIP);
		const genericUpdates = genericFields.map((field) => genericActionsByKey[field.key] ?? ACTIONS.SKIP);
		const textUpdates = textFields.map((field) => textActionsByKey[field.key] ?? ACTIONS.SKIP);
		if ([...fixedUpdates, ...dateUpdates, ...genericUpdates, ...textUpdates].every((action) => action === ACTIONS.SKIP))
			return false;

		if (categoryAction === ACTIONS.SET && !categoryValue) return false;
		if (classAction === ACTIONS.SET && !classValue) return false;
		if (fundAction === ACTIONS.SET && !fundValue) return false;
		if ((badgeAction === ACTIONS.SET || badgeAction === ACTIONS.ADD) && normalizeToArray(badgeValue).length === 0) return false;
		if ((filterAction === ACTIONS.SET || filterAction === ACTIONS.ADD) && normalizeToArray(filterValue).length === 0) return false;
		if ((mechanicAction === ACTIONS.SET || mechanicAction === ACTIONS.ADD) && normalizeToArray(mechanicValue).length === 0) return false;
		for (const field of dateFields) {
			const action = dateActionsByKey[field.key] ?? ACTIONS.SKIP;
			if (action !== ACTIONS.SET)
				continue;
			if (!isDateValueValid(dateValuesByKey[field.key] ?? null, field.dateMode))
				return false;
		}
		for (const field of genericFields) {
			const action = genericActionsByKey[field.key] ?? ACTIONS.SKIP;
			const value = genericValuesByKey[field.key];
			if (field.multiple && (action === ACTIONS.SET || action === ACTIONS.ADD) && normalizeToArray(value).length === 0)
				return false;
			if (!field.multiple && action === ACTIONS.SET && !value)
				return false;
		}
		for (const field of textFields) {
			const action = textActionsByKey[field.key] ?? ACTIONS.SKIP;
			if (action !== ACTIONS.SET && action !== ACTIONS.ADD)
				continue;
			// "Sostituisci" con testo vuoto sarebbe uno "Svuota" mascherato: va scelto esplicitamente.
			const text = String(textValuesByKey[field.key] ?? "").trim();
			if (!(field.isPrice ? normalizePrice(text) : text))
				return false;
		}
		return selectionCount > 0;
	}, [
		textFields,
		textActionsByKey,
		textValuesByKey,
		genericFields,
		genericActionsByKey,
		genericValuesByKey,
		categoryAction,
		categoryValue,
		classAction,
		classValue,
		fundAction,
		fundValue,
		badgeAction,
		badgeValue,
		filterAction,
		filterValue,
		mechanicAction,
		mechanicValue,
		dateFields,
		dateActionsByKey,
		dateValuesByKey,
		selectionCount,
	]);

	const renderActionRow = (
		label,
		action,
		setAction,
		value,
		setValue,
		options,
		searchValue = "",
		setSearchValue = () => {
		},
		onCreate,
	) => {
		const trimmedSearch = String(searchValue || "").trim();
		const lowerSearch = trimmedSearch.toLowerCase();
		const optionExists = options.some((opt) => {
			const comp = String(opt?.searchValue || "").toLowerCase();
			return comp === lowerSearch;
		});

		const handleCreate = async () => {
			if (!onCreate || !trimmedSearch || optionExists) return;
			const result = await onCreate(trimmedSearch);
			if (result && typeof result === "object") {
				const candidate = result.value ?? result.id ?? result._id;
				if (candidate != null) {
					setValue(String(candidate));
					setAction(ACTIONS.SET);
				}
			}
			setSearchValue("");
		};

		return (
			<div className="fb-action-row" key={label}>
				<Text strong>{label}</Text>
				<Segmented
					value={action}
					onChange={(val) => {
						setAction(val);
						if (val !== ACTIONS.SET) setValue(undefined);
					}}
					options={SINGLE_ACTION_OPTIONS}
				/>
				{action === ACTIONS.SET && (
					<Select
						showSearch
						value={value}
						placeholder="Seleziona valore"
						optionFilterProp="searchValue"
						filterOption={(input, option) => {
							const source = String(option?.searchValue || "").toLowerCase();
							return source.includes(String(input).toLowerCase());
						}}
						onChange={(val) => setValue(val)}
						options={options}
						allowClear
						onClear={() => setValue(undefined)}
						onSearch={setSearchValue}
						onInputKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleCreate();
							}
						}}
						onOpenChange={(open) => {
							if (!open) setSearchValue("");
						}}
						popupRender={(menu) => (
							<>
								{menu}
								{onCreate && trimmedSearch && !optionExists && (
									<div className="fb-dropdown-footer">
										<Button
											type="text"
											icon={<PlusOutlined />}
											onMouseDown={(event) => event.preventDefault()}
											onClick={async () => {
												await handleCreate();
											}}
										>
											Aggiungi "{trimmedSearch}"
										</Button>
									</div>
								)}
							</>
						)}
					/>
				)}
			</div>
		);
	};

	const renderMultiActionRow = (
		label,
		action,
		setAction,
		value = [],
		setValue,
		options,
		searchValue = "",
		setSearchValue = () => {
		},
		onCreate,
	) => {
		const trimmedSearch = String(searchValue || "").trim();
		const lowerSearch = trimmedSearch.toLowerCase();
		const optionExists = options.some((opt) => {
			const comp = String(opt?.searchValue || "").toLowerCase();
			return comp === lowerSearch;
		});

		const handleCreate = async () => {
			if (!onCreate || !trimmedSearch || optionExists) return;
			const result = await onCreate(trimmedSearch);
			if (result && typeof result === "object") {
				const candidate = result.value ?? result.id ?? result._id;
				if (candidate != null) {
					setValue((prev) => {
						const next = normalizeToArray(prev);
						if (!next.includes(String(candidate)))
							next.push(String(candidate));
						return next;
					});
					setAction((prev) => (prev === ACTIONS.ADD ? ACTIONS.ADD : ACTIONS.SET));
				}
			}
			setSearchValue("");
		};

		return (
			<div className="fb-action-row" key={label}>
				<Text strong>{label}</Text>
				<Segmented
					value={action}
					onChange={(val) => {
						setAction(val);
						if (val !== ACTIONS.SET && val !== ACTIONS.ADD) setValue([]);
					}}
					options={MULTI_ACTION_OPTIONS}
				/>
				{(action === ACTIONS.SET || action === ACTIONS.ADD) && (
					<Select
						mode="multiple"
						showSearch
						value={normalizeToArray(value)}
						placeholder={action === ACTIONS.ADD ? "Seleziona i valori da aggiungere" : "Seleziona valori"}
						optionFilterProp="searchValue"
						filterOption={(input, option) => {
							const source = String(option?.searchValue || "").toLowerCase();
							return source.includes(String(input).toLowerCase());
						}}
						onChange={(vals) => setValue(normalizeToArray(vals))}
						options={options}
						allowClear
						onClear={() => setValue([])}
						onSearch={setSearchValue}
						onInputKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleCreate();
							}
						}}
						onOpenChange={(open) => {
							if (!open) setSearchValue("");
						}}
						popupRender={(menu) => (
							<>
								{menu}
								{onCreate && trimmedSearch && !optionExists && (
									<div className="fb-dropdown-footer">
										<Button
											type="text"
											icon={<PlusOutlined />}
											onMouseDown={(event) => event.preventDefault()}
											onClick={async () => {
												await handleCreate();
											}}
										>
											Aggiungi "{trimmedSearch}"
										</Button>
									</div>
								)}
							</>
						)}
					/>
				)}
			</div>
		);
	};

	const renderTextActionRow = (field) => {
		const action = textActionsByKey[field.key] ?? ACTIONS.SKIP;
		const value = textValuesByKey[field.key] ?? "";
		const setValue = (next) => setTextValuesByKey((prev) => ({ ...prev, [field.key]: next }));
		const placeholder = action === ACTIONS.ADD ? "Testo da aggiungere in coda" : "Nuovo testo";

		return (
			<div className="fb-action-row" key={`text-${field.key}`}>
				<Text strong>{field.label}</Text>
				<Segmented
					value={action}
					onChange={(val) => {
						setTextActionsByKey((prev) => ({ ...prev, [field.key]: val }));
						if (val !== ACTIONS.SET && val !== ACTIONS.ADD)
							setValue("");
					}}
					options={field.isPrice ? PRICE_ACTION_OPTIONS : TEXT_ACTION_OPTIONS}
				/>
				{(action === ACTIONS.SET || action === ACTIONS.ADD) && (
					field.multiline ? (
						<Input.TextArea
							allowClear
							autoSize={{ minRows: 2, maxRows: 6 }}
							placeholder={placeholder}
							value={value}
							onChange={(e) => setValue(e.target.value)}
						/>
					) : (
						<Input
							allowClear
							placeholder={field.isPrice ? "Es. 1,99" : placeholder}
							value={value}
							onChange={(e) => setValue(e.target.value)}
							onBlur={field.isPrice ? () => setValue(normalizePrice(value)) : undefined}
						/>
					)
				)}
			</div>
		);
	};

	const renderDateActionRow = (field) => {
		const action = dateActionsByKey[field.key] ?? ACTIONS.SKIP;
		const value = dateValuesByKey[field.key] ?? null;

		return (
			<div className="fb-action-row fb-action-compact" key={`date-${field.key}`}>
				<Text strong>{field.label}</Text>
				<Segmented
					value={action}
					onChange={(val) => {
						setDateActionsByKey((prev) => ({ ...prev, [field.key]: val }));
						if (val !== ACTIONS.SET) {
							setDateValuesByKey((prev) => ({ ...prev, [field.key]: null }));
						}
					}}
					options={SINGLE_ACTION_OPTIONS}
				/>
				{action === ACTIONS.SET && (
					field.dateMode === "range" ? (
						<DatePicker.RangePicker
							allowClear
							format="DD/MM/YYYY"
							value={Array.isArray(value) ? value : null}
							onChange={(dates) => {
								setDateValuesByKey((prev) => ({ ...prev, [field.key]: dates || null }));
							}}
							style={{ width: "100%" }}
						/>
					) : (
						<DatePicker
							allowClear
							value={dayjs.isDayjs(value) ? value : null}
							format="DD/MM/YYYY"
							onChange={(nextValue) => {
								setDateValuesByKey((prev) => ({ ...prev, [field.key]: nextValue || null }));
							}}
							style={{ width: "100%" }}
						/>
					)
				)}
			</div>
		);
	};

	return (
		<Modal
			open={open}
			title="Selezione massiva prodotti"
			onCancel={handleClose}
			width={1400}
			style={{ top: 24 }}
			styles={{ body: { overflow: "hidden" } }}
			okText="Applica modifiche"
			onOk={handleApply}
			okButtonProps={{ disabled: !hasChanges, loading }}
			cancelButtonProps={{ disabled: loading }}
			cancelText="Chiudi"
		>
			<div className="fb-meta-bar">
				<div className="fb-meta-left">
					<Text strong style={{ fontSize: 16 }}>Azioni in massa, senza sorprese</Text>
					<Text type="secondary">Espandi le pagine, scegli i prodotti e applica gli attributi in un colpo solo.</Text>
				</div>
				<div className="fb-meta-right">
					<div className="fb-meta-chip">
						<span className="fb-chip-label">Selezionati</span>
						<b>{selectionCount}</b>
						<span className="fb-chip-muted">/{totalProducts || "0"}</span>
					</div>
					<div className="fb-meta-progress">
						<Progress percent={selectionPercent} size="small" showInfo={false} />
					</div>
					<div className="fb-meta-actions">
						<Button size="small" onClick={handleExpandAll}>Espandi pagine</Button>
						<Button size="small" onClick={handleCollapseAll}>Comprimi</Button>
						<Button size="small" type="primary" ghost onClick={handleSelectAll} disabled={!leafKeys.length}>Seleziona tutti</Button>
						<Button size="small" danger ghost onClick={handleClearSelection} disabled={!selectionCount}>Pulisci</Button>
						{onDelete && (
							<Button
								size="small"
								danger
								icon={<DeleteOutlined />}
								onClick={handleBulkDelete}
								disabled={!selectionCount || loading}
							>
								Elimina {selectionCount > 0 ? `(${selectionCount})` : ""}
							</Button>
						)}
					</div>
				</div>
			</div>
			<div className="fb-modal-body">
				<div className="fb-tree-wrap fb-scrollable">
					<div className="fb-tree-card">
						<div className="fb-tree-header">
							<div>
								<Text strong>Prodotti per pagina</Text>
								<Text type="secondary" style={{ display: "block", fontSize: 12 }}>Visualizza solo le gabbie e seleziona rapidamente i prodotti.</Text>
							</div>
							<Tag color="blue" style={{ borderRadius: 999, padding: "2px 10px" }}>{treeData.length} pagine</Tag>
						</div>
						{treeData.length === 0 ? (
							<Empty description="Nessun prodotto disponibile" />
						) : (
							<Tree
								checkable
								draggable={{
									icon: false,
									nodeDraggable: (node) => !String(node.key).startsWith("page-"),
								}}
								onDrop={handleDrop}
								allowDrop={({ dropNode, dropPosition }) => {
									// Permetti drop solo su pagine o tra card
									return true;
								}}
								checkedKeys={checkedKeys}
								onCheck={(keys) => {
									if (Array.isArray(keys))
										setCheckedKeys(keys.map((key) => String(key)),);
									else if (keys?.checked)
										setCheckedKeys(keys.checked.map((key) => String(key)),);
								}}
								expandedKeys={expandedKeys}
								onExpand={(keys) => setExpandedKeys(keys.map((key) => String(key)))}
								blockNode
								selectable={false}
								treeData={treeData}
								style={{ padding: "0 4px" }}
							/>
						)}
					</div>
				</div>
				<div className="fb-actions-wrap fb-scrollable">
					<div className="fb-summary">
						<Text strong style={{ fontSize: "16px" }}>{selectionCount}</Text>
						<Text type="secondary"> {selectionCount === 1 ? "prodotto selezionato" : "prodotti selezionati"}</Text>
					</div>
					{(allowedFields.categoria || dateFields.length > 0) && (
						<div className="fb-action-grid">
							{allowedFields.categoria && renderActionRow(
								"Categoria",
								categoryAction,
								setCategoryAction,
								categoryValue,
								setCategoryValue,
								categorySelectOptions,
								categorySearch,
								setCategorySearch,
								onCreateCategory ? handleCreateCategoryOption : undefined,
							)}
							{dateFields.map((field) => renderDateActionRow(field))}
						</div>
					)}
					{allowedFields.classe && renderActionRow(
						"Classe",
						classAction,
						setClassAction,
						classValue,
						setClassValue,
						classSelectOptions,
						classSearch,
						setClassSearch,
						onCreateClass ? handleCreateClassOption : undefined,
					)}
					{allowedFields.fondi && renderActionRow(
						"Fondi",
						fundAction,
						setFundAction,
						fundValue,
						setFundValue,
						fundSelectOptions,
						fundSearch,
						setFundSearch,
						onCreateFund ? handleCreateFundOption : undefined,
					)}
					{allowedFields.bollino && renderMultiActionRow(
						"Bollino",
						badgeAction,
						setBadgeAction,
						badgeValue,
						setBadgeValue,
						badgeSelectOptions,
						badgeSearch,
						setBadgeSearch,
						onCreateBadge ? handleCreateBadgeOption : undefined,
					)}
					{allowedFields.filtro && renderMultiActionRow(
						"Filtro",
						filterAction,
						setFilterAction,
						filterValue,
						setFilterValue,
						filterSelectOptions,
						filterSearch,
						setFilterSearch,
						onCreateFilter ? handleCreateFilterOption : undefined,
					)}
					{allowedFields.meccanica && renderMultiActionRow(
						"Meccanica",
						mechanicAction,
						setMechanicAction,
						mechanicValue,
						setMechanicValue,
						mechanicSelectOptions,
						mechanicSearch,
						setMechanicSearch,
						onCreateMechanic ? handleCreateMechanicOption : undefined,
					)}
					{genericFields.map((field) => {
						const renderRow = field.multiple ? renderMultiActionRow : renderActionRow;
						const onCreate = genericTaxonomies[field.key]?.onCreate;
						return (
							<React.Fragment key={`generic-${field.key}`}>
								{renderRow(
									field.label,
									genericActionsByKey[field.key] ?? ACTIONS.SKIP,
									makeKeyedSetter(setGenericActionsByKey, field.key),
									field.multiple ? (genericValuesByKey[field.key] ?? []) : genericValuesByKey[field.key],
									makeKeyedSetter(setGenericValuesByKey, field.key),
									genericSelectOptionsByKey[field.key] || [],
									genericSearchByKey[field.key] ?? "",
									makeKeyedSetter(setGenericSearchByKey, field.key),
									onCreate,
								)}
							</React.Fragment>
						);
					})}
					{textFields.map((field) => renderTextActionRow(field))}
					{/*<div className="fb-footer-actions">
						<Button
							onClick={handleSelectAll}
							disabled={loading || treeData.length === 0}
						>
							Seleziona tutti
						</Button>
						<Button
							onClick={handleClearSelection}
							disabled={loading || checkedKeys.length === 0}
						>
							Deseleziona
						</Button>
					</div>*/}
				</div>
			</div>
		</Modal>
	);
}

function PageNode({ page, count, colorBg }) {
	return (
		<div className="fb-page-node" style={{ background: colorBg }}>
			<div className="fb-page-header">
				<Text strong style={{ fontSize: "15px" }}>Pagina {page}</Text>
				<Tag color="blue" style={{ fontWeight: 500 }}>
					{count} {count === 1 ? "prodotto" : "prodotti"}
				</Tag>
			</div>
		</div>
	);
}


function CardNode({ item, getImageUrl, tags }) {
	const thumb = React.useMemo(() => {
		const rel = item?.relPath;
		if (!rel || !getImageUrl) return null;
		return getImageUrl(rel, item?.updatedAt);
	}, [item?.relPath, item?.updatedAt, getImageUrl]);

	const isBanner = isBannerCard(item);

	return (
		<div className={`fb-card-node ${isBanner ? "fb-card-banner" : ""}`}>
			<div className="fb-card-thumb">
				{thumb ? (
					<img src={thumb} alt="" />
				) : (
					<div className="fb-card-placeholder" />
				)}
			</div>
			<div className="fb-card-info">
				<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
					<Text strong ellipsis style={{ fontSize: "14px", flex: 1 }}>
						{isBanner ? (item?.banner?.titolo || "Banner") : (item?.ai?.nome_prodotto || "Prodotto")}
					</Text>
					{isBanner && (
						<Tag color="purple" style={{ fontSize: 10, margin: 0, lineHeight: "16px" }}>Banner</Tag>
					)}
				</div>
				{!isBanner && (item?.ai?.marca || item?.ai?.descrizione) && (
					<Text type="secondary" ellipsis style={{ display: "block", fontSize: "13px", marginBottom: "8px" }}>
						{item?.ai?.marca || item?.ai?.descrizione || ""}
					</Text>
				)}
				{tags.length > 0 && (
					<div className="fb-card-tags" style={{ marginTop: "auto" }}>
						{tags.map((tag) => (
							<Tag key={tag.key} color={tag.color} style={{ fontSize: "11px", marginBottom: "4px" }}>
								<b>{tag.type}:</b> {tag.label}
							</Tag>)
						)}
					</div>
				)}
			</div>
		</div>
	);
}
